import { useEffect } from 'react'
import { useNodeId, useNodesData } from '@xyflow/react'
import type * as Y from 'yjs'
import type { Snapshot } from 'valtio/vanilla'
import { proxy } from 'valtio'
import { bind } from 'valtio-yjs'
import { useSnapshot } from 'valtio/react'
import { createNodeData, type BaseNodeData } from '@/shared/lib/node-registry'
import { useNodeCustomMap } from './useNodeCustomMap'

interface NodeDataCacheEntry {
  nodeData: CustomNodeData
  // 当前 proxy 已绑定的 Y.Map。用于避免同一节点重复 bind。
  boundMap: Y.Map<unknown> | null
  unbind: (() => void) | null
}

type CustomNodeData = BaseNodeData & Record<string, any>

interface UseCustomNodeDataResult<TNodeData extends BaseNodeData> {
  nodeData: TNodeData
  snapshot: Snapshot<TNodeData>
}

// 按 nodeId 复用同一个 Valtio proxy，避免多个组件各自创建 proxy 后绑定同一个 Y.Map。
// 缓存在页面生命周期内保留；React StrictMode 会额外触发 effect cleanup，
// 如果在 cleanup 中 unbind/delete，组件后续可能继续写入一个已经解绑的 proxy。
const nodeDataCache = new Map<string, NodeDataCacheEntry>()

function getNodeDataCacheEntry(nodeId: string, nodeType: string) {
  const defaultData = createNodeData(nodeType)
  const existingEntry = nodeDataCache.get(nodeId)

  if (existingEntry) {
    return existingEntry
  }

  const entry: NodeDataCacheEntry = {
    nodeData: proxy(defaultData) as CustomNodeData,
    boundMap: null,
    unbind: null,
  }

  nodeDataCache.set(nodeId, entry)
  return entry
}

function bindNodeDataCacheEntry(
  nodeId: string,
  yNodeDataMap: Y.Map<unknown>,
) {
  const entry = nodeDataCache.get(nodeId)

  if (!entry || entry.boundMap === yNodeDataMap) {
    return
  }

  // Y.Map 实例可能在同步完成后切换；切换时先解除旧绑定，再绑定新实例。
  entry.unbind?.()
  entry.unbind = bind(entry.nodeData, yNodeDataMap)
  entry.boundMap = yNodeDataMap
}

export function patchNodeDataCache(
  nodeId: string,
  nodeType: string,
  patch: any,
) {
  const entry = getNodeDataCacheEntry(nodeId, nodeType)

  Object.entries(patch).forEach(([key, value]) => {
    entry.nodeData[key] = value
  })
}

export const useCustomNodeData = <
  TNodeData extends BaseNodeData = CustomNodeData,
>(explicitNodeId?: string): UseCustomNodeDataResult<TNodeData> => {
  const contextNodeId = useNodeId()
  const nodeId = explicitNodeId ?? contextNodeId ?? ''

  const flowNodeData = useNodesData(nodeId)
  const nodeType = flowNodeData?.type ?? 'default'
  const entry = getNodeDataCacheEntry(nodeId, nodeType)
  const nodeData = entry.nodeData as unknown as TNodeData
  const yNodeDataMap = useNodeCustomMap(nodeId)

  useEffect(() => {
    if (!yNodeDataMap) {
      return
    }

    bindNodeDataCacheEntry(nodeId, yNodeDataMap)
  }, [nodeId, yNodeDataMap])

  return { nodeData, snapshot: useSnapshot(nodeData) }
}
